import { useLoadScript } from "@react-google-maps/api";
import { Button, Col, Form, Input, message, RadioChangeEvent, Row } from "antd";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckBoxList from "../../../commons/components/CheckBoxList/CheckBoxList";
import CustomDropImageInput from "../../../commons/components/CustomDropImageInput/CustomDropImageInput";
import CustomInput from "../../../commons/components/CustomInput/CustomInput";
import { CustomSelect } from "../../../commons/components/CustomSelect/CustomSelect";
import FormBox from "../../../commons/components/FormBox/FormBox";
import FormHeader from "../../../commons/components/FormHeader/FormHeader";
import GradientText from "../../../commons/components/GradientText/GradientText";
import PrimaryButton from "../../../commons/components/PrimaryButton/PrimaryButton";
import RadioBoxList from "../../../commons/components/RadioBoxList/RadioBoxList";
import TextArea from "../../../commons/components/TextArea/TextArea";
import { API_KEY, MediaDirectoryEnum, PHONE_NUMBER_PATTERN, StoreExample } from "../../../commons/constants";
import {
  CATEGORIES_CHILDREN_URL,
  CATEGORIES_PARENT_URL,
  CREATE_STORE_ADDRESS_REQUEST_URL,
  CREATE_STORE_REQUEST_URL,
  PLATFORM_MIN_ORDER,
  UPDATE_STORE_ADDRESS_REQUEST_URL,
  UPDATE_STORE_REQUEST_URL,
} from "../../../commons/constants/api-urls";
import { routerNavigation } from "../../../commons/constants/routers";
import {
  BINARY_OPTIONS,
  CLONE_STORE_OPTIONS,
  PRE_ORDER_SERVICE_SUPPORT_OPTIONS,
  SERVICE_SUPPORT_OPTIONS,
  SERVICE_SUPPORTS,
  STORE_STATUS,
  STORE_TYPE,
  STORE_TYPE_OPTIONS,
} from "../../../commons/constants/store";
import defaultAxios from "../../../commons/utils/axios";
import { numberInputOnWheelPreventChange } from "../../../commons/utils/functions/helper";
import {
  decimalOnlyInput,
  filterECharacterInputDecimal,
  filterECharacterInputNumber,
} from "../../../commons/utils/functions/integerOnly";
import removeExtraSpace, { addTwoDecimal } from "../../../commons/utils/functions/removeExtraSpace";
import { displayStoreId } from "../../../commons/utils/functions/shortText";
import {
  getAddressFromStoreValues,
  getBodyFromStoreValues,
  getValuesFromStoreDetail,
} from "../../../commons/utils/functions/storeMapping";
import { uploadImageToServer } from "../../../commons/utils/functions/uploadImageToServer";
import { AppContext } from "../../../contexts/AppContext";
import PlacesAutoComplete from "../../Map/PlacesAutoComplete";
import styles from "./CreateUpdateStoreForm.module.scss";
import OpeningHours from "./OpeningHours";
import { CustomStatus } from "../../../commons/components/CustomStatus/CustomStatus";

type Props = {
  store?: Store | null;
  address?: StoreAddress;
  isCloning?: boolean;
  setOpenClone: React.Dispatch<React.SetStateAction<boolean>>;
};

const CreateUpdateStoreForm = ({ store, address, isCloning, setOpenClone }: Props) => {
  const { user, setCurrentStore, refreshAll } = useContext(AppContext);
  const navigate = useNavigate();
  const [form] = Form.useForm<CreateUpdateStoreValues>();
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [cateOptionLevel2, setCateOptionLevel2] = useState<{ value: string; label: string }[]>([]);
  const [cateOptionLevel3, setCateOptionLevel3] = useState<{ value: string; label: string }[]>([]);
  const [platformMinOrders, setPlatformMinOrders] = useState<{
    min_order: number;
    deliveryMinOrder: number;
    pickupMinOrder: number;
  }>({
    min_order: 0,
    deliveryMinOrder: 0,
    pickupMinOrder: 0,
  });
  const [orderSupportError, setOrderSupportError] = useState<boolean>(false);
  const type = Form.useWatch(["type"], form);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: CreateUpdateStoreValues) => {
    // check this error in Food Delivery case
    if (
      type === STORE_TYPE.FOOD_DELIVERY &&
      values.service_supports.length === 0 &&
      values.preOrderServices.length === 0
    ) {
      form.setFields([
        {
          name: "preOrderServices",
          errors: ["Please select at least one option for order or pre-order delivery."],
        },
      ]);
      setOrderSupportError(true);
      return;
    }
    setLoading(true);
    try {
      const { logo, banners } = values;
      const bannerUrl = banners?.filter(banner => typeof banner === "string") as string[];
      const newBanners = banners?.filter(banner => typeof banner !== "string");
      const logoUrl = typeof logo[0] === "string" ? logo[0] : await uploadImageToServer(logo, MediaDirectoryEnum.STORE);
      if (newBanners) {
        await uploadImageToServer(newBanners, MediaDirectoryEnum.STORE).then(res => {
          if (Array.isArray(res)) {
            res.forEach(item => bannerUrl.push(item));
          } else {
            bannerUrl.push(res);
          }
        });
      }

      const body = {
        ...getBodyFromStoreValues(values),
        logo: logoUrl,
        banners: bannerUrl,
        merchantId: user?.merchantIds?.[0],
        originalStoreId: values?.clone_products?.[0] && store?.id,
      };

      const addressBody = getAddressFromStoreValues(values);

      if (!user?.merchantIds?.[0]) return;
      const res =
        store?.id && !isCloning
          ? await defaultAxios.put<CreateUpdateStoreResponse>(UPDATE_STORE_REQUEST_URL.concat(`/${store.id}`), body)
          : await defaultAxios.post<CreateUpdateStoreResponse>(CREATE_STORE_REQUEST_URL, body);

      address?.id && !isCloning
        ? await defaultAxios.put(UPDATE_STORE_ADDRESS_REQUEST_URL, {
            ...addressBody,
            merchantStoreApprovalId: res.data.id,
          })
        : await defaultAxios.post(CREATE_STORE_ADDRESS_REQUEST_URL(res.data.id), addressBody);
      message.success(store?.id ? "Save change successfully" : "Create store successfully");
      setCurrentStore(StoreExample);
      refreshAll();
      navigate(routerNavigation.STORES.LIST);
    } catch (error: any) {
      console.log(error);
      message.error(store?.id ? "Save change failed" : "Create store failed");
    }
    setLoading(false);
  };

  async function handleGetPlatformMinOrders() {
    try {
      const res = await defaultAxios.get(PLATFORM_MIN_ORDER());
      setPlatformMinOrders(res.data);
    } catch (error) {
      console.log(error);
    }
  }

  async function handleGetCategory() {
    try {
      const res = await defaultAxios.get(CATEGORIES_PARENT_URL);
      setOptions(res?.data?.map((category: Category) => ({ value: category.id, label: category.name })) || []);
      return res.data;
    } catch (error) {
      console.log(error);
    }
  }

  async function handleGetChildCategory(parentId: string) {
    try {
      const res = await defaultAxios.get(CATEGORIES_CHILDREN_URL, { params: { categoryIds: parentId } });
      return res.data;
    } catch (error) {
      console.log(error);
    }
  }

  async function handleGetListSubCategoryLevel2(value: string[]) {
    if (value.length === 0) {
      setCateOptionLevel2([]);
      setCateOptionLevel3([]);
      return;
    }
    try {
      const cateLevel2 = await handleGetChildCategory(value.join(","));
      const listCate = cateLevel2.map((cate: any) => {
        return cate.children?.map((category: Category) => ({ value: category.id, label: category.name }));
      });
      setCateOptionLevel2(listCate.flat());
    } catch (error) {
      console.log(error);
    }
  }

  async function handleGetListSubCategoryLevel3(value: string[]) {
    if (value.length === 0) return setCateOptionLevel3([]);
    try {
      const cateLevel3 = await handleGetChildCategory(value.join(","));
      const listCate = cateLevel3.map((cate: any) => {
        return cate.children?.map((category: Category) => ({ value: category.id, label: category.name }));
      });

      setCateOptionLevel3(listCate.flat());
    } catch (error) {
      console.log(error);
    }
  }

  const handleCancel = () => {
    if (store?.id) {
      form.resetFields();
      navigate(routerNavigation.STORES.LIST);
    } else navigate(-1);
  };

  const handleChangeStoreType = (e: RadioChangeEvent) => {
    const data = e.target.value;
    form.setFieldValue("type", data);
    // For surplus type
    if (data === STORE_TYPE.SURPLUS) {
      form.setFieldValue("service_supports", [SERVICE_SUPPORTS.PICKUP]);
      form.setFieldValue("preOrderServices", []);
    } else if (data === STORE_TYPE.FOOD_DELIVERY) {
      form.setFieldValue("service_supports", store?.service_supports);
      form.setFieldValue("preOrderServices", store?.preOrderServices);
    }
  };

  const isOpen24Hour = Form.useWatch(["isOpen24Hour"], form);

  const showDataWithSurplusType = useMemo(() => {
    if (!store || !store?.id) return true;
    if (store.type === STORE_TYPE.SURPLUS) return false;

    return true;
  }, [store]);

  const isUpdatingStore = useMemo(() => {
    return store?.id ? true : false;
  }, [store]);

  const isHavingRequestUpdate = useMemo(() => {
    return store?.merchantStoreApproval && store?.merchantStoreApproval.length > 0 ? true : false;
  }, [store]);

  const isOnboardingStore = useMemo(() => {
    return store?.status === STORE_STATUS.PENDING ? true : false;
  }, [store]);

  useEffect(() => {
    form.setFieldsValue(getValuesFromStoreDetail(store, address));
    handleGetListSubCategoryLevel2(form.getFieldValue("categoryLevel1Ids"));
    handleGetListSubCategoryLevel3(form.getFieldValue("categoryLevel2Ids"));
  }, [store, address, form]);

  useEffect(() => {
    if (!store) {
      form.setFieldValue("pickupMinOrder", platformMinOrders.pickupMinOrder);
      form.setFieldValue("deliveryMinOrder", platformMinOrders.deliveryMinOrder);
    }
  }, [platformMinOrders]);

  useEffect(() => {
    handleGetCategory();
  }, []);

  useEffect(() => {
    handleGetPlatformMinOrders();
  }, []);

  const handleStoreId = () => {
    if (store?.id) {
      return (
        <div className={styles.storeStatusBox}>
          <div className={styles.storeIdBox}>
            <p>
              Store ID: <span>#{displayStoreId(store.id)}</span>
            </p>
            {store.triFoodPOSes.length > 0 && (
              <p>
                POS ID: <span>#{store.triFoodPOSes.map(item => item.POSId).join(", ")}</span>
              </p>
            )}
          </div>
          <div className={styles.storeStatus}>
            <p>
              Status: <span>{STORE_STATUS[store.status]}</span>
            </p>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  };
  const StoreId = useMemo(handleStoreId, [store]);

  const StoreAction = () => {
    return (
      <>
        <Button type="text" className={styles.cancel} onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        {store?.id && (
          <Button type="primary" className={styles.clone} onClick={() => setOpenClone && setOpenClone(true)}>
            Clone
          </Button>
        )}

        <Form.Item noStyle shouldUpdate={() => true}>
          {() => {
            return (
              <Button
                type="primary"
                htmlType="submit"
                className={styles.save}
                disabled={loading || !!form.getFieldsError().find(item => item.errors.length)}
              >
                {loading ? "Saving" : "Save"}
              </Button>
            );
          }}
        </Form.Item>
      </>
    );
  };

  const StoreHasPendingUpdateRequest = () => {
    return <div className={styles.warning}>This store still has a pending request</div>;
  };

  const formTitle = useMemo(() => {
    if (isCloning) {
      return <p className={styles.storeName}>Clone Store {store?.name}</p>;
    } else if (store?.name) {
      return <>{store?.name}</>;
    } else {
      return <>Create new store</>;
    }
  }, [store]);

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <>
      <Form
        form={form}
        name="create-store"
        wrapperCol={{ span: 24 }}
        initialValues={getValuesFromStoreDetail(store, address)}
        onFinish={onFinish}
        onError={e => console.log(e)}
        autoComplete="off"
        scrollToFirstError={true}
      >
        <FormHeader
          title={formTitle}
          actions={
            isCloning ? (
              <></>
            ) : (
              <>
                {isHavingRequestUpdate || isOnboardingStore ? (
                  <>
                    <StoreHasPendingUpdateRequest />
                  </>
                ) : (
                  <>
                    <StoreAction />
                  </>
                )}
              </>
            )
          }
        />

        <FormBox
          title={
            <div className={styles.titleWrapper}>
              <div>Basic information</div>
              {store && (
                <CustomStatus
                  label={store.type === STORE_TYPE.SURPLUS ? STORE_TYPE_OPTIONS[1].label : STORE_TYPE_OPTIONS[0].label}
                  borderColor={store.type === STORE_TYPE.SURPLUS ? "rgba(193, 168, 117, 1)" : "rgba(18, 183, 106, 1)"}
                  textColor={store.type === STORE_TYPE.SURPLUS ? "rgba(193, 168, 117, 1)" : "rgba(18, 183, 106, 1)"}
                  className={styles.storeType}
                />
              )}
            </div>
          }
        >
          {StoreId}

          <CustomInput
            name="name"
            label="Store name"
            onBlur={removeExtraSpace("name", form)}
            placeholder="Enter your Store name"
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            maxLength={120}
          />
          <CustomDropImageInput
            name="logo"
            label="Store logo"
            square
            isSingle={true}
            multiple={1}
            onError={err => form?.setFields([{ name: "logo", errors: [err] }])}
            rules={[{ required: true, message: "This field is required" }]}
            isEdit={store?.id ? true : false}
            form={form}
            isCheckRation={false}
            description={<p style={{ marginTop: "12px" }}> Image ratio will be 1:1</p>}
          />

          <CustomDropImageInput
            name="banners"
            label="Store banners"
            multiple={3}
            onError={err => form?.setFields([{ name: "banners", errors: [err] }])}
            description={
              <>
                <p>Up to 3 banners</p>
                <p>Aspect will be 16:9</p>
              </>
            }
            rules={[{ required: true, message: "This field is required" }]}
            isEdit={store?.id ? true : false}
            form={form}
            aspect={2}
          />

          <RadioBoxList
            name="type"
            hidden={!!store?.id && !!store}
            rules={[{ required: true, message: "This field is required" }]}
            label="Store Type"
            options={STORE_TYPE_OPTIONS}
            onChange={handleChangeStoreType}
            groupClassName={styles.storeType}
          />

          <CheckBoxList
            name="service_supports"
            label="Delivery & Pick-up"
            disabled={type === STORE_TYPE.SURPLUS}
            options={SERVICE_SUPPORT_OPTIONS}
            groupClassName={styles.serviceSupports}
            groupProps={{
              hidden: !showDataWithSurplusType,
            }}
            onChange={value => {
              form.setFields([
                {
                  name: "preOrderServices",
                  errors: [],
                },
                {
                  name: "service_supports",
                  errors: [],
                  value: value,
                },
              ]);
              setOrderSupportError(false);
            }}
          />

          <CheckBoxList
            name="preOrderServices"
            label="Pre-Order Options"
            disabled={type === STORE_TYPE.SURPLUS}
            options={PRE_ORDER_SERVICE_SUPPORT_OPTIONS}
            groupClassName={styles.serviceSupports}
            onChange={value => {
              form.setFields([
                {
                  name: "preOrderServices",
                  errors: [],
                  value: value,
                },
                {
                  name: "service_supports",
                  errors: [],
                },
              ]);
              setOrderSupportError(false);
            }}
            groupProps={{
              hidden: !showDataWithSurplusType,
              extra: !orderSupportError && (
                <span className={styles.extraText}>
                  (Please select at least one option for order or pre-order delivery)
                </span>
              ),
            }}
          />

          <p className={`${styles.title} ${styles.astrik}`}>Category Level 1</p>
          <CustomSelect
            rules={[{ required: true, message: "This field is required" }]}
            placeholder="Choose business categories"
            name="categoryLevel1Ids"
            options={options}
            defaultValue={options?.[0]?.value}
            mode="multiple"
            allowClear
            className={styles.businessCategory}
            onChange={handleGetListSubCategoryLevel2}
          />

          {cateOptionLevel2?.length > 0 && (
            <>
              <p className={styles.title}>Category Level 2</p>
              <CustomSelect
                rules={[{ required: false, message: "This field is required" }]}
                placeholder="Choose business sub categories level 2"
                name="categoryLevel2Ids"
                options={cateOptionLevel2}
                mode="multiple"
                allowClear
                className={styles.businessCategory}
                onChange={handleGetListSubCategoryLevel3}
              />
            </>
          )}

          {cateOptionLevel3?.length > 0 && (
            <>
              <p className={styles.title}>Category Level 3</p>
              <CustomSelect
                rules={[{ required: false, message: "This field is required" }]}
                placeholder="Choose business sub categories level 3"
                name="categoryLevel3Ids"
                options={cateOptionLevel3}
                mode="multiple"
                allowClear
                className={styles.businessCategory}
              />
            </>
          )}

          <CustomInput
            name="deliveryMinOrder"
            disabled={type === STORE_TYPE.SURPLUS}
            label={`Minimum delivery order (Please setup if need higher than $${platformMinOrders.deliveryMinOrder})`}
            onBlur={e => {
              removeExtraSpace("deliveryMinOrder", form);
              addTwoDecimal("deliveryMinOrder", form, e);
            }}
            onWheel={numberInputOnWheelPreventChange}
            placeholder="0.00"
            onKeyDown={decimalOnlyInput}
            onChange={event => filterECharacterInputDecimal("deliveryMinOrder", form, event)}
            prefix={<div className={styles.preFix}>$</div>}
            rules={[
              {
                validator: (_, value) => {
                  //validate input must be greater than 0
                  if (value && value < platformMinOrders.deliveryMinOrder) {
                    return Promise.reject(
                      new Error(`Minimum delivery order must be greater than ${platformMinOrders.deliveryMinOrder}`)
                    );
                  }

                  if (value && value.toString().startsWith(".")) {
                    return Promise.reject(new Error("Invalid number"));
                  }
                  return Promise.resolve();
                },
              },
              {
                required: true,
                message: "This field is required",
              },
            ]}
          />

          <CustomInput
            name="pickupMinOrder"
            label={`Minimum pickup order (Please setup if need higher than $${platformMinOrders.pickupMinOrder})`}
            onBlur={e => {
              removeExtraSpace("pickupMinOrder", form);
              addTwoDecimal("pickupMinOrder", form, e);
            }}
            onWheel={numberInputOnWheelPreventChange}
            placeholder="0.00"
            onKeyDown={decimalOnlyInput}
            onChange={event => filterECharacterInputDecimal("pickupMinOrder", form, event)}
            prefix={<div className={styles.preFix}>$</div>}
            rules={[
              {
                validator: (_, value) => {
                  //validate input must be greater than 0
                  if (value && value < platformMinOrders.pickupMinOrder) {
                    return Promise.reject(
                      new Error(`Minimum pickup order must be greater than ${platformMinOrders.pickupMinOrder}`)
                    );
                  }

                  if (value && value.toString().startsWith(".")) {
                    return Promise.reject(new Error("Invalid number"));
                  }
                  return Promise.resolve();
                },
              },
              {
                required: true,
                message: "This field is required",
              },
            ]}
          />

          <TextArea
            name="description"
            label="Description"
            onBlur={removeExtraSpace("description", form)}
            placeholder="Tell us something about your store"
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            showCount
            maxLength={3000}
          />

          <Row gutter={24}>
            <Col xs={24} sm={24}>
              <CustomInput
                name="outletContactPerson"
                label="Outlet contact person"
                onBlur={removeExtraSpace("outletContactPerson", form)}
                placeholder="Enter your outlet contact person"
                rules={[
                  {
                    required: false,
                    message: "This field is required",
                    whitespace: true,
                  },
                ]}
                maxLength={120}
              />
            </Col>
          </Row>
          <RadioBoxList
            name={"halalCertified"}
            label={"Halal Certification"}
            options={BINARY_OPTIONS}
            rules={[{ required: true, message: "This field is required" }]}
            groupClassName={styles.serviceSupports}
          />
          <RadioBoxList
            name={"muslimOwned"}
            label={"Muslim Owned"}
            options={BINARY_OPTIONS}
            rules={[{ required: true, message: "This field is required" }]}
            groupClassName={styles.serviceSupports}
          />
          {/* <RadioBoxList
          name={"hasOwnDeliveryFleet"}
          label={"Delivery Fleet"}
          options={BINARY_OPTIONS}
          rules={[{ required: true, message: "This field is required" }]}
          groupClassName={styles.serviceSupports}
        /> */}
          <RadioBoxList
            name="isOpen24Hour"
            label="Open 24 hours"
            options={BINARY_OPTIONS}
            rules={[{ required: true, message: "This field is required" }]}
            groupClassName={styles.serviceSupports}
          />
          {!isOpen24Hour && (
            <>
              <OpeningHours form={form} isUpdatingStore={isUpdatingStore} />
            </>
          )}
        </FormBox>
        <FormBox title="Store address">
          <CustomInput
            prefix="+65 |"
            name="phone"
            label="Phone number"
            onChange={event => filterECharacterInputNumber("phone", form, event)}
            rules={[
              { required: true, message: "This field is required" },
              { pattern: PHONE_NUMBER_PATTERN, message: "Phone number invalid" },
            ]}
            placeholder="Input your phone number"
            onBlur={removeExtraSpace("phone", form)}
          />
          <Form.Item name="coordinate" noStyle>
            <Input hidden />
          </Form.Item>
          <Form.Item noStyle dependencies={["coordinate"]}>
            {({ getFieldValue, setFieldValue, setFieldsValue, validateFields }) => (
              <PlacesAutoComplete
                selected={getFieldValue("coordinate")}
                setSelected={value => {
                  setFieldsValue({ coordinate: value });
                  setFieldValue("coordinate", value);
                }}
                defaultAddress={getFieldValue("address")}
                validateAddress={() => validateFields(["address"])}
                setLocation={data => {
                  setFieldValue("address", data?.description || "");
                }}
              />
            )}
          </Form.Item>
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <CustomInput
                name="location_type"
                label="Landmark"
                rules={[{ required: false, message: "This field is required" }]}
                placeholder="Landmark"
                maxLength={100}
                onBlur={removeExtraSpace("location_type", form)}
              />
            </Col>
            <Col xs={24} sm={12}>
              <CustomInput
                name="postal_code"
                label="Postal code"
                rules={[{ required: true, message: "This field is required" }]}
                placeholder="Postal code"
                maxLength={6}
                onChange={event => filterECharacterInputNumber("postal_code", form, event)}
              />
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <CustomInput
                name="city_or_province"
                label="City"
                rules={[{ required: true, message: "This field is required" }]}
                placeholder="City"
                onBlur={removeExtraSpace("city_or_province", form)}
                maxLength={20}
              />
            </Col>
            <Col xs={24} sm={12}>
              <CustomInput
                name="country"
                label="Country"
                rules={[{ required: true, message: "This field is required" }]}
                placeholder="Country"
                maxLength={20}
                onBlur={removeExtraSpace("country", form)}
              />
            </Col>
          </Row>
        </FormBox>
        {isCloning && (
          <>
            <FormBox>
              <CheckBoxList
                name="clone_products"
                options={CLONE_STORE_OPTIONS}
                rules={[{ required: false, message: "This field is required" }]}
                groupClassName={styles.serviceSupports}
              />
            </FormBox>
            <div className={styles.groupButton}>
              <div
                className={styles.btnCancel}
                onClick={() => {
                  setOpenClone && setOpenClone(false);
                }}
              >
                <GradientText text={"Cancel"}></GradientText>
              </div>
              <PrimaryButton className={styles.btnConfirm} disabled={loading}>
                Confirm
              </PrimaryButton>
            </div>
          </>
        )}
      </Form>
    </>
  );
};

export default CreateUpdateStoreForm;
